import { useId } from "react";

import { useFormContext } from "react-hook-form";

import { FormField } from "@ps-design-system/components/Form";
import { InputField } from "@ps-design-system/components/Input";
import { Switch } from "@ps-design-system/components/Switch";
import { Text } from "@ps-design-system/components/Text";

import { useIntl } from "react-intl-compat";

import { MIN_SPACE_RENTAL } from "../ListingFsvForm.consts";
import type { ListingFsvFormValues } from "../ListingFsvForm.schema";
import { isBlankAmount } from "../ListingFsvForm.utils";

type SpaceRentalFieldProps = {
  currencySymbol: string;
  onBlur: () => void;
  onFocus: () => void;
};

const SpaceRentalField = ({
  currencySymbol,
  onBlur,
  onFocus,
}: SpaceRentalFieldProps) => {
  const intl = useIntl();
  const toggleLabelId = useId();
  const { control, getValues, setValue } =
    useFormContext<ListingFsvFormValues>();

  // Raise anything under the minimum before the pricing check reads it;
  // blank is left for the form's validation
  const handleBlur = () => {
    const spaceRental = getValues("spaceRental");
    if (!isBlankAmount(spaceRental) && Number(spaceRental) < MIN_SPACE_RENTAL) {
      setValue("spaceRental", MIN_SPACE_RENTAL, {
        shouldDirty: true,
        shouldValidate: true,
      });
    }
    onBlur();
  };

  return (
    <FormField
      control={control}
      name="hasSpaceRental"
      render={({ field }) => (
        <div className="tw-mb-16" data-testid="fsv-form-space-rental-field">
          <div className="tw-flex tw-items-center tw-justify-between tw-gap-16 tw-pt-8">
            <Text id={toggleLabelId} typeface="body-2-m" element="p">
              {intl.formatMessage({
                id: "hosting.listing_fsv.space_rental_toggle_label",
                defaultMessage: "Do you charge a space rental?",
              })}
            </Text>
            <Switch
              aria-labelledby={toggleLabelId}
              checked={field.value}
              onCheckedChange={field.onChange}
              data-testid="fsv-form-space-rental-toggle"
            />
          </div>

          {field.value && (
            // InputField overrides its own onBlur with the form's, so listen here
            <div className="tw-mt-16" onBlur={handleBlur} onFocus={onFocus}>
              <InputField
                fullWidth
                name="spaceRental"
                label={intl.formatMessage({
                  id: "hosting.listing_fsv.space_rental_label",
                  defaultMessage: "Space rental amount",
                })}
                subcopy={intl.formatMessage({
                  id: "hosting.listing_fsv.space_rental_subcopy",
                  defaultMessage: "A flat fee to reserve the space.",
                })}
                type="number"
                min={MIN_SPACE_RENTAL}
                step={1}
                prefix={currencySymbol}
                testId="fsv-form-space-rental-input"
              />
            </div>
          )}
        </div>
      )}
    />
  );
};

export { SpaceRentalField };
